import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { theme } from './theme';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyOtpPage from './pages/VerifyOtpPage';
import DashboardPage from './pages/DashboardPage';
import CreatePostPage from './pages/CreatePostPage';
import PostsPage from './pages/PostsPage';
import EditPostPage from './pages/EditPostPage';
import TrendingPage from './pages/TrendingPage';
import SchedulePage from './pages/SchedulePage';
import AnalyticsPage from './pages/AnalyticsPage';
import VoicePage from './pages/VoicePage';
import WebsiteAnalysisPage from './pages/WebsiteAnalysisPage';
import { NewVideoScriptsPage } from './pages/NewVideoScriptsPage';
import TranslationPage from './pages/TranslationPage';
import AccessibilityPage from './pages/AccessibilityPage';
import BrandConsistencyPage from './pages/BrandConsistencyPage';
import EventTriggersPage from './pages/EventTriggersPage';
import IntegrationsPage from './pages/IntegrationsPage';
import SocialIntegrationsPage from './pages/SocialIntegrationsPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import EmergencyPage from './pages/EmergencyPage';
import { ApiTester } from './components/ApiTester';
import OAuthCallbackPage from './components/OAuthCallback';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { getCurrentUser } from './store/slices/authSlice';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import PublicLayout from './components/Layout/PublicLayout';
import LandingPage from './pages/marketing/LandingPage';
import AboutPage from './pages/marketing/AboutPage';
import ContactPage from './pages/marketing/ContactPage';
import PrivacyPolicyPage from './pages/marketing/PrivacyPolicyPage';
import TermsPage from './pages/marketing/TermsPage';
import NotFoundPage from './pages/marketing/NotFoundPage';

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector(state => state.auth);
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// Admin Route Component (protect admin routes)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  const isAdmin = user?.is_superuser || 
                  user?.roles?.includes('admin') || 
                  user?.roles?.includes('superuser');
  
  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

function AppContent() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector(state => state.auth);

  // Fetch user data if authenticated but user data is missing
  React.useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(getCurrentUser());
    }
  }, [isAuthenticated, user, dispatch]);

  return (
    <Router>
      <Routes>
        {/* Marketing / public site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Auth routes */}
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/verify-email" element={
          <PublicRoute>
            <VerifyOtpPage />
          </PublicRoute>
        } />

        {/* Protected Routes with Layout */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/create-post" element={
          <ProtectedRoute>
            <Layout>
              <CreatePostPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/posts" element={
          <ProtectedRoute>
            <Layout>
              <PostsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/posts/edit/:id" element={
          <ProtectedRoute>
            <Layout>
              <EditPostPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/trending" element={
          <ProtectedRoute>
            <Layout>
              <TrendingPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={
          <ProtectedRoute>
            <Layout>
              <SchedulePage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <Layout>
              <AnalyticsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/voice" element={
          <ProtectedRoute>
            <Layout>
              <VoicePage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/website-analysis" element={
          <ProtectedRoute>
            <Layout>
              <WebsiteAnalysisPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/video-scripts" element={
          <ProtectedRoute>
            <Layout>
              <NewVideoScriptsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/translation" element={
          <ProtectedRoute>
            <Layout>
              <TranslationPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/accessibility" element={
          <ProtectedRoute>
            <Layout>
              <AccessibilityPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/brand" element={
          <ProtectedRoute>
            <Layout>
              <BrandConsistencyPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/events" element={
          <ProtectedRoute>
            <Layout>
              <EventTriggersPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/api-test" element={
          <ProtectedRoute>
            <Layout>
              <ApiTester />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute>
            <Layout>
              <IntegrationsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/social-integrations" element={
          <ProtectedRoute>
            <Layout>
              <SocialIntegrationsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        } />


        {/* OAuth Callback Routes (no layout needed) */}
        <Route path="/oauth/callback/linkedin" element={
          <ProtectedRoute>
            <OAuthCallbackPage />
          </ProtectedRoute>
        } />
        <Route path="/oauth/callback/x" element={
          <ProtectedRoute>
            <OAuthCallbackPage />
          </ProtectedRoute>
        } />
        <Route path="/oauth/callback/instagram" element={
          <ProtectedRoute>
            <OAuthCallbackPage />
          </ProtectedRoute>
        } />
        <Route path="/oauth/callback/facebook" element={
          <ProtectedRoute>
            <OAuthCallbackPage />
          </ProtectedRoute>
        } />

        {/* Integration Success Callback Route */}
        <Route path="/integrations/success" element={<OAuthCallbackPage />} />
        <Route path="/integrations/error" element={<OAuthCallbackPage />} />

        {/* Admin Routes */}
        <Route path="/admin/*" element={
          <AdminRoute>
            <Layout>
              <Routes>
                <Route index element={<AdminPage />} />
                <Route path="users" element={<AdminPage />} />
                <Route path="roles" element={<AdminPage />} />
                <Route path="plans" element={<AdminPage />} />
                <Route path="analytics" element={<AdminPage />} />
                <Route path="emergency" element={<EmergencyPage />} />
              </Routes>
            </Layout>
          </AdminRoute>
        } />

        {/* Default redirects */}
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
        <Toaster position="top-right" />
      </ThemeProvider>
    </Provider>
  );
}

export default App; 